// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Agent Inbox Query
 *
 * Single-call surface for an agent's active workload:
 *  - Tasks they're assigned to (Planned + In Progress)
 *  - Discussions where they were @-mentioned (within the lookback window)
 *  - Replies on threads they authored OR where they were @-mentioned in the reply
 *
 * Design spec: docs/specs/2026-05-03-agent-inbox-design.md §2
 *
 * Pure library: depends only on the in-memory collection accessor shape
 * defined by `AgentInboxCollections`. CLI and MCP surfaces (Phase 3) wire
 * up the accessors against RxDB; tests pass mocks.
 */

import type { DiscussionMention, DiscussionModel } from '../types'
import type { TaskModel } from '../models/Task'
import type { ConversationModel } from '../models/Conversation'
import type { MessageModel } from '../models/Message'
import type { ApprovalModel } from '../models/Approval'
import type { MilestoneModel } from '../models/Milestone'
import type { ProjectModel } from '../models/Project'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface TaskInboxItem {
  id: string
  title: string
  description?: string
  status: string
  priority: number
  projectId?: string
  milestoneId?: string
  parentTaskId?: string
  createdAt: string
  updatedAt: string
  dueAt?: string
}

export interface MentionInboxItem {
  discussionId: string
  entityType: string
  entityId: string
  authorTeamMemberId: string
  authorUserName: string
  contentSnippet: string
  position: number
  createdAt: string
  /** Set when the discussion's entity is one of MY active tasks. */
  relatedTaskId?: string
}

export interface ReplyInboxItem {
  discussionId: string
  parentDiscussionId: string
  authorTeamMemberId: string
  authorUserName: string
  contentSnippet: string
  myPriorSnippet?: string
  reason: 'authored-thread' | 'mentioned-in-reply'
  createdAt: string
  /** Set when the discussion's entity is one of MY active tasks. */
  relatedTaskId?: string
}

/**
 * Multi-agent conversation surfaced in the inbox. One entry per
 * `conversations` row where the agent is a participant. New since
 * proj_qMizgu4RD2 (Conversations Extension); see spec §3.
 */
export interface ConversationInboxItem {
  conversationId: string
  title: string
  participantIds: string[]
  /** Resolved at query time when `collections.teammembers` is available. */
  participantSlugs?: string[]
  /** True when the conversation was implicitly created from a DM. */
  isDm: boolean
  /** True when the agent has muted this thread (`metadata.mutedBy` contains me). */
  isMuted: boolean
  /** Count of messages in window where `userId !== me`. */
  unreadCount: number
  /** Any unread message has me in `mentions[]` (per the hybrid match). */
  hasUnreadMention: boolean
  /** Denormalized from `metadata.lastMessageAt` (set by d1-worker hook). */
  lastMessageAt: string
  /** First 200 chars of the last message; from `metadata.lastMessagePreview`. */
  lastMessagePreview: string
  /** teamMemberId of the last message author. */
  lastMessageAuthorId: string
  /** Resolved slug for the last message author. */
  lastMessageAuthorSlug?: string
  /** Self-reported confidence (0-1) on the latest unread message, when set. */
  lastMessageConfidence?: number
  // Turn policy fields (when present on the conversation):
  turnsUsed?: number
  maxTurns?: number
  turnsRemaining?: number
  /** True when `turnsUsed >= maxTurns`. */
  policyExceeded?: boolean
  /** True when `turnPolicy.closedAt` is set. */
  isClosed?: boolean
  /** From `turnPolicy.closedReason` when the conversation is closed. */
  closedReason?: string
  /** Original requester's confidence target from `turnPolicy.expectedConfidence`. */
  expectedConfidence?: number
}

/**
 * Pending approval where the caller is the approver. Surfaced in the
 * inbox as a separate section because they have a different action shape
 * from mentions/replies (response is a `collection-update`, not a new
 * discussion) and a different urgency profile (most workflows block
 * downstream tasks until the approval lands).
 *
 * `approverId` on the wire is a single string — agents = `team_xxx`,
 * humans = `user_xxx`. The query just matches whichever id the caller
 * carries.
 */
export interface ApprovalInboxItem {
  /** `appr_xxx` — full id, used directly in the response `collection-update`. */
  id: string
  title: string
  /** Free-form type label (e.g. "brainstorm-question", "phase-decomposition"). */
  type: string
  /** Workflow category slug (e.g. "brainstorming", "planning"). */
  category: string
  /** Human-readable category label (e.g. "Brainstorming"). */
  categoryName: string
  /** Free-form status — pending entries appear here; resolved ones don't. */
  status: string
  /** Optional preview of `documentContent` (first 200 chars). */
  contentSnippet?: string
  /** Optional doc-type discriminator, e.g. "proposals", "phase-decomposition". */
  documentType?: string
  /** Cross-references to the entity awaiting approval — populated when set. */
  projectId?: string
  milestoneId?: string
  taskId?: string
  documentId?: string
  /** Approval creator (the one who *requested* the approval). */
  requestedByUserId: string
  createdAt: string
}

/**
 * Mention parsed from a task / milestone / project `description` field
 * (PR C). Distinct from {@link MentionInboxItem} (which is sourced from
 * `discussions`) because the action surface is different — to clear a
 * description-mention you typically reply with a `discussions` post on
 * the entity, not edit the description.
 */
export interface DescriptionMentionInboxItem {
  /** Collection the entity lives in: `'tasks' | 'milestones' | 'projects'`. */
  entityType: 'task' | 'milestone' | 'project'
  /** Full entity id with native prefix (`task_<12>`, `mile_<12>`, `proj_<12>`). */
  entityId: string
  /** Entity title — agent's primary anchor when reading the inbox. */
  title: string
  /** First 200 chars of the description so the agent has context. */
  descriptionSnippet: string
  /** Position of my @-handle inside the description. */
  position: number
  /** Last write time (`updatedAt`) — relative-time anchor. */
  updatedAt: string
}

export interface AgentInboxResult {
  tasks: TaskInboxItem[]
  mentions: MentionInboxItem[]
  replies: ReplyInboxItem[]
  /** Multi-agent conversations where I'm a participant (Phase 2 of proj_qMizgu4RD2). */
  conversations: ConversationInboxItem[]
  /** Pending approvals where I'm the approver. */
  approvals: ApprovalInboxItem[]
  /** PR C: @-mentions found in task/milestone/project `description` fields. */
  descriptionMentions: DescriptionMentionInboxItem[]
  /** ISO 8601 timestamp of when the query ran. */
  generatedAt: string
  /** ISO 8601 cutoff (now - sinceDays) used to filter discussions/replies. */
  windowStart: string
}

/**
 * Identity for an inbox query. Discriminated union so the same code path
 * serves both agents (queried by `teamMemberId` + `slug`) and humans
 * (queried by `userId`). Conversations are skipped when `kind === 'user'`
 * — agent-to-agent threads aren't a human surface today.
 */
export type InboxIdentity = AgentInboxIdentity | UserInboxIdentity

export interface AgentInboxIdentity {
  kind: 'agent'
  teamMemberId: string
  slug: string
  orgId: string
  workspaceId: string
}

export interface UserInboxIdentity {
  kind: 'user'
  userId: string
  orgId: string
  workspaceId: string
}

/**
 * Single id used as the equality target for the bulk of selectors:
 * `tasks.assigneeId`, `discussions.userId` (the author), `messages.userId`,
 * etc. Agents = `team_xxx`, humans = `user_xxx`.
 */
function callerId(identity: InboxIdentity): string {
  return identity.kind === 'agent' ? identity.teamMemberId : identity.userId
}

export interface AgentInboxOpts {
  /** Lookback window in days. Default 7. */
  sinceDays?: number
  /** Per-signal cap (tasks, mentions, replies). Default 50. */
  limit?: number
}

/**
 * Minimal accessor shape backing the inbox query.
 *
 * `find` accepts a Mango-style selector and optional sort/limit; concrete
 * implementations may translate this to RxDB queries, D1 SELECTs, or
 * in-memory filters (the test path).
 */
export interface AgentInboxCollections {
  tasks: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<TaskModel[]>
  }
  discussions: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<DiscussionModel[]>
    findByIds(ids: string[]): Promise<DiscussionModel[]>
  }
  /**
   * Optional — when not provided, `AgentInboxResult.conversations` is empty.
   * Phase 2 of proj_qMizgu4RD2.
   */
  conversations?: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<ConversationModel[]>
  }
  /**
   * Optional — required only when `conversations` accessor is provided.
   */
  messages?: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<MessageModel[]>
  }
  /**
   * Optional — when provided, `ConversationInboxItem.participantSlugs` and
   * `lastMessageAuthorSlug` are resolved via a single batched lookup.
   */
  teammembers?: {
    findByIds(
      ids: string[]
    ): Promise<Array<{ id: string; slug?: string | null; userName?: string | null }>>
  }
  /**
   * Optional — when not provided, `AgentInboxResult.approvals` is empty.
   * Pending approvals where the caller is the `approverId`.
   */
  approvals?: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<ApprovalModel[]>
  }
  /**
   * Optional — when ANY of these three accessors are wired, the inbox
   * scans the corresponding collection's `mentions[]` field. Missing
   * accessor → that collection is silently skipped (so a caller can
   * mount only the collections they care about).
   *
   * The `tasks` accessor is reused from above for description-mention
   * scanning; we don't add a separate one. Milestones and projects
   * each get their own here.
   */
  milestones?: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<MilestoneModel[]>
  }
  projects?: {
    find(query: {
      selector: Record<string, unknown>
      sort?: Array<Record<string, unknown>>
      limit?: number
    }): Promise<ProjectModel[]>
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const SNIPPET_LEN = 200
const MY_PRIOR_SNIPPET_LEN = 100

const ACTIVE_TASK_STATUSES = [
  'Planned',
  'planned',
  'In Progress',
  'in_progress',
  'in-progress',
  'todo',
]

function snippet(content: string, n: number): string {
  if (typeof content !== 'string') return ''
  return content.length <= n ? content : content.slice(0, n)
}

/**
 * Hybrid mention match: true when ANY entry in `mentions[]` resolves to me.
 *
 * For agents, the discussion mention shape carries either a stored
 * `teamMemberId` (resolved at write time) or a literal `identifier` (the
 * slug as typed). Match either.
 *
 * For humans, the mention shape carries `userId` (resolved at write
 * time). The literal-identifier escape hatch isn't applicable — humans
 * don't have a stable `@<handle>` slug today.
 */
function mentionsMatchMe(
  mentions: DiscussionMention[] | undefined,
  identity: InboxIdentity
): boolean {
  if (!mentions || mentions.length === 0) return false
  if (identity.kind === 'agent') {
    const slugLower = identity.slug.toLowerCase()
    return mentions.some(
      m =>
        m.teamMemberId === identity.teamMemberId ||
        (typeof m.identifier === 'string' && m.identifier.toLowerCase() === slugLower)
    )
  }
  return mentions.some(m => m.userId === identity.userId)
}

function findMyMentionPosition(
  mentions: DiscussionMention[] | undefined,
  identity: InboxIdentity
): number {
  if (!mentions || mentions.length === 0) return 0
  if (identity.kind === 'agent') {
    const slugLower = identity.slug.toLowerCase()
    const found = mentions.find(
      m =>
        m.teamMemberId === identity.teamMemberId ||
        (typeof m.identifier === 'string' && m.identifier.toLowerCase() === slugLower)
    )
    return found?.position ?? 0
  }
  const found = mentions.find(m => m.userId === identity.userId)
  return found?.position ?? 0
}

function dedupReplies(
  authored: DiscussionModel[],
  mentioned: DiscussionModel[]
): Array<{ discussion: DiscussionModel; reason: 'authored-thread' | 'mentioned-in-reply' }> {
  const seen = new Set<string>()
  const out: Array<{
    discussion: DiscussionModel
    reason: 'authored-thread' | 'mentioned-in-reply'
  }> = []
  // authored-thread first (preferred reason on overlap)
  for (const r of authored) {
    if (seen.has(r.id)) continue
    seen.add(r.id)
    out.push({ discussion: r, reason: 'authored-thread' })
  }
  for (const r of mentioned) {
    if (seen.has(r.id)) continue
    seen.add(r.id)
    out.push({ discussion: r, reason: 'mentioned-in-reply' })
  }
  return out
}

function toTaskInboxItem(t: TaskModel): TaskInboxItem {
  return {
    id: t.id,
    title: t.title,
    description: t.description,
    status: t.status,
    priority: t.priority,
    projectId: t.projectId,
    milestoneId: t.milestoneId,
    parentTaskId: t.parentTaskId,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
    dueAt: t.dueAt,
  }
}

function toMentionInboxItem(
  d: DiscussionModel,
  myTaskIds: Set<string>,
  identity: InboxIdentity
): MentionInboxItem {
  const item: MentionInboxItem = {
    discussionId: d.id,
    entityType: d.entityType,
    entityId: d.entityId,
    authorTeamMemberId: d.userId,
    authorUserName: d.userName,
    contentSnippet: snippet(d.content, SNIPPET_LEN),
    position: findMyMentionPosition(d.mentions, identity),
    createdAt: d.createdAt,
  }
  if (d.entityType === 'task' && myTaskIds.has(d.entityId)) {
    item.relatedTaskId = d.entityId
  }
  return item
}

function toReplyInboxItem(
  d: DiscussionModel,
  reason: 'authored-thread' | 'mentioned-in-reply',
  parentMap: Map<string, DiscussionModel>,
  myTaskIds: Set<string>,
  identity: InboxIdentity
): ReplyInboxItem {
  const parent = d.parentId ? parentMap.get(d.parentId) : undefined

  const item: ReplyInboxItem = {
    discussionId: d.id,
    parentDiscussionId: d.parentId ?? '',
    authorTeamMemberId: d.userId,
    authorUserName: d.userName,
    contentSnippet: snippet(d.content, SNIPPET_LEN),
    reason,
    createdAt: d.createdAt,
  }

  if (parent && parent.userId === callerId(identity)) {
    item.myPriorSnippet = snippet(parent.content, MY_PRIOR_SNIPPET_LEN)
  }

  if (d.entityType === 'task' && myTaskIds.has(d.entityId)) {
    item.relatedTaskId = d.entityId
  }

  return item
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build an agent's active inbox.
 *
 * @param collections - Accessors for `tasks` and `discussions` collections.
 * @param identity - The agent's resolved identity (teamMemberId, slug, tenancy).
 * @param opts - Optional sinceDays (default 7) and per-signal limit (default 50).
 *
 * @returns AgentInboxResult — three independent arrays (tasks, mentions, replies)
 *          plus `generatedAt` / `windowStart` for caller display.
 */
export async function getInbox(
  collections: AgentInboxCollections,
  identity: InboxIdentity,
  opts?: AgentInboxOpts
): Promise<AgentInboxResult> {
  const sinceDays = opts?.sinceDays ?? 7
  const limit = opts?.limit ?? 50
  const generatedAt = new Date().toISOString()
  const windowStart = new Date(Date.now() - sinceDays * 86400_000).toISOString()
  const me = callerId(identity)

  const [tasks, candidateDiscussions, candidateReplies] = await Promise.all([
    collections.tasks.find({
      selector: {
        assigneeId: me,
        status: { $in: ACTIVE_TASK_STATUSES },
        completed: { $ne: true },
        archived: { $ne: true },
        _deleted: 0,
        orgId: identity.orgId,
        workspaceId: identity.workspaceId,
      },
      sort: [{ priority: 'desc' }, { createdAt: 'desc' }],
      limit,
    }),
    collections.discussions.find({
      selector: {
        createdAt: { $gte: windowStart },
        _deleted: 0,
        orgId: identity.orgId,
        workspaceId: identity.workspaceId,
      },
      sort: [{ createdAt: 'desc' }],
      // Overfetch so we can TS-side filter on hybrid mention match.
      limit: limit * 5,
    }),
    collections.discussions.find({
      selector: {
        parentId: { $exists: true, $ne: null },
        createdAt: { $gte: windowStart },
        _deleted: 0,
        orgId: identity.orgId,
        workspaceId: identity.workspaceId,
        userId: { $ne: me },
      },
      sort: [{ createdAt: 'desc' }],
      limit: limit * 5,
    }),
  ])

  // Filter mentions by hybrid match
  const mentionMatches = candidateDiscussions.filter(d => mentionsMatchMe(d.mentions, identity))

  // The candidate-replies Mango selector uses
  // `parentId: { $exists: true, $ne: null }`. The empty-string convention
  // for "no parent" (`""`) is a string that exists and is not `null`, so it
  // passes the selector. Strip those top-level discussions TS-side so they
  // only show up under `mentions[]`, not duplicated under `replies[]`.
  const actualReplies = candidateReplies.filter(
    r => typeof r.parentId === 'string' && r.parentId.length > 0
  )

  // Resolve parent discussions for the reply two-pass walk
  const parentIds = Array.from(
    new Set(actualReplies.map(r => r.parentId).filter((id): id is string => Boolean(id)))
  )
  const parents = parentIds.length > 0 ? await collections.discussions.findByIds(parentIds) : []
  const parentMap = new Map(parents.map(p => [p.id, p]))

  const repliesAuthored = actualReplies.filter(r => {
    if (!r.parentId) return false
    const parent = parentMap.get(r.parentId)
    return parent?.userId === me
  })
  const repliesMentioned = actualReplies.filter(r => mentionsMatchMe(r.mentions, identity))

  const replyEntries = dedupReplies(repliesAuthored, repliesMentioned)

  // Build cross-reference set of "my task ids"
  const myTaskIds = new Set(tasks.map(t => t.id))

  // Phase 2 of proj_qMizgu4RD2 (Conversations Extension): aggregate
  // multi-agent conversations the agent is a participant of. Optional —
  // when `collections.conversations` and `collections.messages` aren't
  // wired (e.g. a caller using only the base inbox), conversations is `[]`.
  const conversations = await aggregateConversations(collections, identity, windowStart, limit)

  // Pending approvals where I'm the approver. Single-key index hit on
  // `approverId`; status is filtered TS-side because the schema uses a
  // free-form string and the active set might evolve. `windowStart` is
  // intentionally NOT applied — pending approvals don't expire on the
  // lookback window; an approval requested 30 days ago that's still
  // pending should still show up.
  const approvals = await aggregateApprovals(collections, identity, limit)

  // PR C: @-mentions parsed from task / milestone / project descriptions.
  // Same forward-only indexing model as `discussions.mentions`. Each
  // collection's accessor is independently optional; missing → skipped.
  const descriptionMentions = await aggregateDescriptionMentions(
    collections,
    identity,
    windowStart,
    limit
  )

  return {
    tasks: tasks.slice(0, limit).map(toTaskInboxItem),
    mentions: mentionMatches.slice(0, limit).map(d => toMentionInboxItem(d, myTaskIds, identity)),
    replies: replyEntries
      .slice(0, limit)
      .map(({ discussion, reason }) =>
        toReplyInboxItem(discussion, reason, parentMap, myTaskIds, identity)
      ),
    conversations,
    approvals,
    descriptionMentions,
    generatedAt,
    windowStart,
  }
}

/**
 * Phase 2 of proj_qMizgu4RD2: aggregate the agent's conversation inbox.
 *
 * Returns `[]` when the optional `conversations` / `messages` accessors
 * aren't wired (caller using only the base inbox surfaces).
 *
 * Steps:
 *  1. Fetch agent-chat conversations where the agent is a participant.
 *  2. Per conversation: fetch unread messages in window (`userId !== me`).
 *  3. Build the inbox item with unreadCount, hasUnreadMention, lastMessage*
 *     (from the parent conversation's metadata), turnPolicy snapshot.
 *  4. Mute filter — skip muted conversations UNLESS hasUnreadMention is true
 *     (mute can't suppress @-mentions).
 *  5. Sort by lastMessageAt desc; slice to limit.
 *  6. Optional: resolve participantSlugs + lastMessageAuthorSlug via batched
 *     teammembers.findByIds when the accessor is present.
 */
async function aggregateConversations(
  collections: AgentInboxCollections,
  identity: InboxIdentity,
  windowStart: string,
  limit: number
): Promise<ConversationInboxItem[]> {
  if (!collections.conversations || !collections.messages) return []
  // Multi-agent conversations are an agent-only surface today. Humans
  // don't currently participate in agent-chat threads (no `team_xxx`
  // identifier to match on participantIds). Skip cleanly so the user
  // inbox doesn't 500 against the unrelated query path.
  if (identity.kind === 'user') return []

  // Agent-chat conversations are scoped to the org, NOT the workspace —
  // teammembers themselves live at the org level (no workspaceId), so a
  // DM between two teammembers should be visible regardless of which
  // workspace either agent happens to be operating in. Filtering by
  // `workspaceId` here would orphan any conversation whose row carries
  // a different workspaceId than the inbox caller's tm row (the bug that
  // hid CFO-initiated DMs from CEO/CTO on 2026-05-23 — the writer
  // defaulted workspaceId to '' while the reader resolved it to the
  // tm row's value).
  const candidates = await collections.conversations.find({
    selector: {
      'metadata.kind': 'agent-chat',
      'metadata.participantIds': { $in: [identity.teamMemberId] },
      _deleted: 0,
      orgId: identity.orgId,
    },
    sort: [{ updatedAt: 'desc' }],
    limit: limit * 2,
  })
  if (candidates.length === 0) return []

  // Single batched fetch of all unread messages across the candidate
  // conversations. Avoids the N+1 query pattern (one find per conv) AND
  // the per-conversation row cap (which would have made `unreadCount`
  // and `hasUnreadMention` lossy for very chatty threads).
  const candidateIds = candidates.map(c => c.id)
  const allUnread = await collections.messages.find({
    selector: {
      conversationId: { $in: candidateIds },
      createdAt: { $gte: windowStart },
      userId: { $ne: identity.teamMemberId },
      _deleted: 0,
      // Messages inherit the conversation's org scope; workspaceId is
      // intentionally omitted to match the parent-conversation selector
      // above. Cross-org isolation is enforced via orgId.
      orgId: identity.orgId,
    },
    sort: [{ createdAt: 'desc' }],
    // Per-call cap: the inbox only surfaces ~limit conversations, each
    // typically with <50 unread; even at the upper bound this stays
    // bounded. If a single conversation legitimately has more, the
    // count is still accurate (we group client-side); only the
    // `lastMessageConfidence` heuristic uses the truncated list.
    limit: limit * 200,
  })

  // Group by conversationId
  const unreadByConvId = new Map<string, MessageModel[]>()
  for (const m of allUnread) {
    const cid = m.conversationId
    if (typeof cid !== 'string') continue
    if (!unreadByConvId.has(cid)) unreadByConvId.set(cid, [])
    unreadByConvId.get(cid)!.push(m)
  }

  // Build inbox items + apply mute filter
  const items: ConversationInboxItem[] = []
  for (const c of candidates) {
    const meta = (c.metadata as Record<string, unknown> | undefined) ?? {}
    const unread = unreadByConvId.get(c.id) ?? []
    const item = buildConversationInboxItem(c, meta, unread, identity)

    const mutedBy = (meta.mutedBy as string[] | undefined) ?? []
    const isMuted = mutedBy.includes(identity.teamMemberId)
    if (isMuted && !item.hasUnreadMention) continue
    item.isMuted = isMuted
    items.push(item)
  }

  // Sort by lastMessageAt desc (already sorted by updatedAt desc, but
  // lastMessageAt is the canonical ordering for inbox display).
  items.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
  const sliced = items.slice(0, limit)

  // Resolve participant + author slugs (single batched lookup) when the
  // teammembers accessor is provided.
  if (collections.teammembers && sliced.length > 0) {
    const tmIds = new Set<string>()
    for (const i of sliced) {
      i.participantIds.forEach(id => tmIds.add(id))
      if (i.lastMessageAuthorId) tmIds.add(i.lastMessageAuthorId)
    }
    const tmList = Array.from(tmIds)
    if (tmList.length > 0) {
      const rows = await collections.teammembers.findByIds(tmList)
      const slugById = new Map<string, string>()
      for (const r of rows) {
        if (r.slug) slugById.set(r.id, r.slug)
        else if (r.userName) slugById.set(r.id, r.userName)
      }
      for (const i of sliced) {
        i.participantSlugs = i.participantIds.map(id => slugById.get(id) ?? id)
        if (i.lastMessageAuthorId) {
          i.lastMessageAuthorSlug = slugById.get(i.lastMessageAuthorId) ?? i.lastMessageAuthorId
        }
      }
    }
  }

  return sliced
}

function buildConversationInboxItem(
  c: ConversationModel,
  meta: Record<string, unknown>,
  unread: MessageModel[],
  identity: AgentInboxIdentity // user kind is filtered out before this is called
): ConversationInboxItem {
  const participantIds = (meta.participantIds as string[] | undefined) ?? []
  const isDm = meta.isDm === true
  const lastMessageAt = (meta.lastMessageAt as string | undefined) ?? c.updatedAt
  const lastMessagePreview = (meta.lastMessagePreview as string | undefined) ?? ''
  const lastMessageAuthorId = (meta.lastMessageAuthorId as string | undefined) ?? ''

  const hasUnreadMention = unread.some(m =>
    mentionsMatchMe(m.mentions as DiscussionMention[] | undefined, identity)
  )

  // Latest unread message's self-reported confidence (when set on its metadata)
  let lastMessageConfidence: number | undefined
  for (const m of unread) {
    const mm = (m.metadata as Record<string, unknown> | undefined) ?? {}
    if (typeof mm.confidence === 'number') {
      lastMessageConfidence = mm.confidence
      break
    }
  }

  const item: ConversationInboxItem = {
    conversationId: c.id,
    title: c.title,
    participantIds,
    isDm,
    isMuted: false, // set by caller after filtering
    unreadCount: unread.length,
    hasUnreadMention,
    lastMessageAt,
    lastMessagePreview,
    lastMessageAuthorId,
  }
  if (lastMessageConfidence != null) item.lastMessageConfidence = lastMessageConfidence

  // Turn policy fields (when set on the conversation)
  const tp = meta.turnPolicy as Record<string, unknown> | undefined
  if (tp) {
    if (typeof tp.turnsUsed === 'number') item.turnsUsed = tp.turnsUsed
    if (typeof tp.maxTurns === 'number') {
      item.maxTurns = tp.maxTurns
      item.turnsRemaining = Math.max(0, tp.maxTurns - (item.turnsUsed ?? 0))
      item.policyExceeded = (item.turnsUsed ?? 0) >= tp.maxTurns
    }
    if (typeof tp.expectedConfidence === 'number') item.expectedConfidence = tp.expectedConfidence
    if (typeof tp.closedAt === 'string' && tp.closedAt.length > 0) {
      item.isClosed = true
      if (typeof tp.closedReason === 'string') item.closedReason = tp.closedReason
    }
  }

  return item
}

/**
 * Pending-approval aggregator. Single tenancy-scoped query on
 * `approverId == me, _deleted = 0`, with `status` filtered TS-side
 * (free-form string in the schema; the active set evolves over time
 * — `'pending'` is the canonical value but defensive matching keeps
 * the inbox tolerant to legacy or new variants).
 *
 * Returns `[]` when the optional `collections.approvals` accessor
 * isn't wired so callers using only the base inbox surfaces don't
 * pay for a query they didn't ask for.
 */
async function aggregateApprovals(
  collections: AgentInboxCollections,
  identity: InboxIdentity,
  limit: number
): Promise<ApprovalInboxItem[]> {
  if (!collections.approvals) return []

  const me = callerId(identity)
  const candidates = await collections.approvals.find({
    selector: {
      approverId: me,
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
    },
    sort: [{ createdAt: 'desc' }],
    // Overfetch so the TS-side status filter still has `limit` items
    // worth of pending entries to surface even if half the rows are
    // resolved (a busy approver might have many recent decisions).
    limit: limit * 5,
  })

  const pending = candidates.filter(a => isPendingStatus(a.status))
  return pending.slice(0, limit).map(toApprovalInboxItem)
}

function isPendingStatus(status: string | undefined): boolean {
  if (typeof status !== 'string') return false
  // 'pending' is the canonical value used by every brainstorming /
  // multi-phase planning flow. Lowercased compare so the inbox is
  // tolerant if a caller writes 'Pending' instead.
  return status.toLowerCase() === 'pending'
}

function toApprovalInboxItem(a: ApprovalModel): ApprovalInboxItem {
  const item: ApprovalInboxItem = {
    id: a.id,
    title: a.title,
    type: a.type,
    category: a.category,
    categoryName: a.categoryName,
    status: a.status,
    requestedByUserId: a.userId,
    createdAt: a.createdAt,
  }
  if (a.documentContent) item.contentSnippet = snippet(a.documentContent, SNIPPET_LEN)
  if (a.documentType) item.documentType = a.documentType
  // Cross-reference fields are required strings in the schema but most
  // approvals only set a subset (e.g. project-level approvals leave
  // taskId empty). Surface only the populated ones so the agent doesn't
  // chase empty-string ids.
  if (a.projectId) item.projectId = a.projectId
  if (a.milestoneId) item.milestoneId = a.milestoneId
  if (a.taskId) item.taskId = a.taskId
  if (a.documentId) item.documentId = a.documentId
  return item
}

/**
 * Description-mention aggregator (PR C). Scans `tasks`, `milestones`,
 * and `projects` collections for any active row where `mentions[]`
 * matches the caller. Each collection's accessor is independently
 * optional — missing → skipped without crashing.
 *
 * The scan is windowed by `updatedAt >= windowStart` so the result set
 * doesn't grow unbounded as the org accumulates entities. Existing rows
 * whose descriptions were authored BEFORE the worker write hook landed
 * have `mentions = null` and are silently skipped — same forward-only
 * disclosure as discussions.
 *
 * Status filter (TS-side): only `tasks` enforces `status IN active set`.
 * Milestones and projects can have @-mentions even when "Complete" — the
 * agent might be tagged for a retrospective comment. So those two
 * collections only filter on tenancy + window + non-empty mentions.
 */
async function aggregateDescriptionMentions(
  collections: AgentInboxCollections,
  identity: InboxIdentity,
  windowStart: string,
  limit: number
): Promise<DescriptionMentionInboxItem[]> {
  const out: DescriptionMentionInboxItem[] = []

  // ---------- tasks ----------
  // Tasks accessor is required (already used for the Active Tasks
  // section), so always run this branch.
  const taskHits = await collections.tasks.find({
    selector: {
      _deleted: 0,
      orgId: identity.orgId,
      workspaceId: identity.workspaceId,
      // Don't constrain on status — a description-mention on a
      // Complete task is still actionable (e.g. retro comment).
      updatedAt: { $gte: windowStart },
    },
    sort: [{ updatedAt: 'desc' }],
    limit: limit * 5,
  })
  for (const t of taskHits) {
    if (!mentionsMatchMe(t.mentions as DiscussionMention[] | undefined, identity)) continue
    out.push({
      entityType: 'task',
      entityId: t.id,
      title: t.title,
      descriptionSnippet: snippet(t.description ?? '', SNIPPET_LEN),
      position: findMyMentionPosition(t.mentions as DiscussionMention[] | undefined, identity),
      updatedAt: t.updatedAt,
    })
  }

  // ---------- milestones ----------
  if (collections.milestones) {
    const milestoneHits = await collections.milestones.find({
      selector: {
        _deleted: 0,
        orgId: identity.orgId,
        workspaceId: identity.workspaceId,
        updatedAt: { $gte: windowStart },
      },
      sort: [{ updatedAt: 'desc' }],
      limit: limit * 5,
    })
    for (const m of milestoneHits) {
      if (!mentionsMatchMe(m.mentions as DiscussionMention[] | undefined, identity)) continue
      out.push({
        entityType: 'milestone',
        entityId: m.id,
        title: m.title,
        descriptionSnippet: snippet(m.description ?? '', SNIPPET_LEN),
        position: findMyMentionPosition(m.mentions as DiscussionMention[] | undefined, identity),
        updatedAt: m.updatedAt,
      })
    }
  }

  // ---------- projects ----------
  if (collections.projects) {
    const projectHits = await collections.projects.find({
      selector: {
        _deleted: 0,
        orgId: identity.orgId,
        workspaceId: identity.workspaceId,
        updatedAt: { $gte: windowStart },
      },
      sort: [{ updatedAt: 'desc' }],
      limit: limit * 5,
    })
    for (const p of projectHits) {
      if (!mentionsMatchMe(p.mentions as DiscussionMention[] | undefined, identity)) continue
      out.push({
        entityType: 'project',
        entityId: p.id,
        title: p.title,
        descriptionSnippet: snippet(p.description ?? '', SNIPPET_LEN),
        position: findMyMentionPosition(p.mentions as DiscussionMention[] | undefined, identity),
        updatedAt: p.updatedAt,
      })
    }
  }

  // Sort all hits across the three collections by updatedAt desc and cap.
  out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return out.slice(0, limit)
}

/**
 * Backward-compat alias for the agent-only inbox surface.
 *
 * @deprecated Use {@link getInbox} with an {@link InboxIdentity}
 * (discriminated union) instead. Existing call sites that pass a literal
 * of the old shape just need a `kind: 'agent'` discriminator added to
 * compile against the new typing.
 */
export const getAgentInbox = getInbox
